import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, MessageCircle } from "lucide-react";
import { BELogoMark } from "./BELogo";
import { waLink } from "../lib/wa";

const NAV_LINKS = [
  { label: "Model Lineup", to: "/models" },
  { label: "Attachments", to: "/attachments" },
  { label: "Parts & Service", to: "/parts-service" },
  { label: "Contact Us", to: "/#contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [pathname, hash]);

  const handleAnchor = (e, to) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      if (pathname !== "/") {
        navigate(to);
      } else {
        const id = to.replace("/#", "");
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const isActive = (to) => {
    if (to === "/#contact") return false;
    return pathname === to || (to !== "/" && pathname.startsWith(to));
  };

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 header-orange shadow-sm"
      data-testid="site-navbar"
    >
      {/* top micro-strip */}
      <div className="hidden md:flex items-center justify-between px-6 md:px-10 h-8 text-[11px] tracking-[0.18em] uppercase text-white/80 bg-[#d94d00]/30">
        <div>Authorised Dealer · Bobcat India · Pan-India Service</div>
        <div className="flex items-center gap-5">
          <a href={waLink({ source: "header_strip" })} target="_blank" rel="noreferrer" className="hover:text-white flex items-center gap-1">
            <MessageCircle size={12} /> +91 91676 32596
          </a>
          <a href="mailto:info@bhumiequipments.com" className="hover:text-white">info@bhumiequipments.com</a>
        </div>
      </div>

      {/* main nav row */}
      <div className="px-4 sm:px-6 md:px-10 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center shrink-0">
          <BELogoMark variant="white" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9 mx-auto">
          {NAV_LINKS.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={(e) => handleAnchor(e, l.to)}
              className={`nav-link text-[13px] tracking-[0.08em] uppercase font-semibold text-white/90 hover:text-white transition-colors ${
                isActive(l.to) ? "active text-white" : ""
              }`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <Link
            to="/book-demo"
            className="inline-flex items-center gap-1.5 bg-white text-[#ff6a13] text-[13px] font-bold tracking-[0.06em] uppercase px-5 py-2.5 rounded-full hover:bg-black hover:text-white transition-colors"
            data-testid="nav-book-demo-btn"
          >
            Book a Demo <ArrowUpRight size={15} />
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#ff6a13] border-t border-white/20">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to)}
                className="text-base font-semibold tracking-wide text-white"
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/book-demo"
              className="self-start mt-2 bg-white text-[#ff6a13] px-5 py-2.5 rounded-full font-bold text-sm"
            >
              Book a Demo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
